import React from 'react';
import { Box, InputAdornment } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DecimalField from './DecimalField';
import VatModeToggle from './VatModeToggle';
import VatConverseLine from './VatConverseLine';

/**
 * THE price input: a DecimalField with the VAT-inclusive/exclusive entry toggle
 * and a converse-price hint ("= 37,37 € alv 0"). Use this for every price
 * field so entry mode is always explicit and displays stay consistent.
 *
 * Controlled: `value` is the price in the CURRENT mode (`withVat`). Toggling
 * does NOT touch the value. It only declares whether that number is net or
 * gross, and reports the new mode via `onModeChange(newWithVat, value)`; the
 * converse line under the field shows the price the other way round. This is
 * the app-wide rule, the same one the work-order dialogs have always used.
 * Margin-scheme prices are always gross: pass `lockedInclVat` to pin the mode
 * and hide the toggle.
 *
 * Seed `withVat` from the org's `default_price_includes_vat` for new lines and
 * from the stored `price_entered_with_vat` when editing.
 */
function VatPriceField({
  value,
  onChange,
  withVat,
  onModeChange,
  vatRate = 25.5,
  lockedInclVat = false,
  labelInclVat,
  labelExclVat,
  showConverse = true,
  size = 'small',
  sx,
  ...decimalFieldProps
}) {
  const { t } = useTranslation();
  const inclMode = lockedInclVat || withVat;

  const handleModeChange = (e, newMode) => {
    if (newMode === null || lockedInclVat) return;
    const newWithVat = newMode === 'incl';
    if (newWithVat === withVat) return;
    // The typed number never moves. Only its meaning changes, and the caller
    // re-derives net/gross from (value, mode).
    onModeChange(newWithVat, value);
  };

  return (
    <Box sx={sx}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <DecimalField
          label={inclMode ? (labelInclVat ?? t('parts_sales.unitPriceGross')) : (labelExclVat ?? t('parts_sales.unitPriceNet'))}
          value={value}
          onChange={onChange}
          size={size}
          InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
          sx={{ flex: 1, minWidth: 110 }}
          {...decimalFieldProps}
        />
        {!lockedInclVat && (
          <VatModeToggle
            value={inclMode ? 'incl' : 'excl'}
            onChange={handleModeChange}
            sx={{ mt: size === 'small' ? 0.25 : 0.75, flexShrink: 0 }}
          />
        )}
      </Box>
      {showConverse && !lockedInclVat && (
        <VatConverseLine
          value={value}
          mode={inclMode ? 'incl' : 'excl'}
          vatRate={vatRate}
          sx={{ display: 'block', mt: 0.25, ml: 0.5 }}
        />
      )}
    </Box>
  );
}

export default VatPriceField;
